import { makeAutoObservable, runInAction } from "mobx";

import client from "@/client";
import { graphql } from "@/gql";
import {
  CreateFormInput,
  FormElementAttributes,
  FormElementKind,
  UpdateFormInput,
} from "@/gql/graphql";

import FormElement from "./FormElement";
import Section from "./Section";

export default class Form {
  id = "";
  title = "";
  sections: Section[] = [];

  constructor(sectionsCount = 0) {
    makeAutoObservable(this);

    for (let index = 0; index < sectionsCount; index++) {
      this.addSection();
    }
  }

  async load(id: string): Promise<void> {
    const { data } = await client.query({
      query: formQuery,
      variables: { id },
    });
    runInAction(() => {
      if (!data.form) return;

      this.id = data.form.id;
      this.title = data.form.title;
      this.sections = [];

      let currentSection: Section;

      data.form.elements.nodes.forEach((node, i) => {
        // First element is not a section, this is an old form before sections.
        if (i === 0 && node.kind !== FormElementKind.Section) {
          currentSection = this.addSection();
          currentSection.id = node.id;
          currentSection.arguments = node.arguments ?? {};
        }

        // Data from backend is not nested, sections and elements are inside
        // `elements` flat. A section element marks the beginning of a new
        // section of elements in the array.
        if (node.kind === FormElementKind.Section) {
          currentSection = this.addSection();
          currentSection.id = node.id;
          currentSection.arguments = node.arguments ?? {};
          return;
        }

        const element = new FormElement(currentSection, node.kind);
        element.id = node.id;
        element.arguments = node.arguments ?? {};
        element.required = node.required;
        element.singleFile = node.singleFile
          ? new File([], node.singleFile.filename)
          : undefined;
        element.singleFileUrl = node.singleFile?.url;
        element.singleFileSignedId = node.singleFile?.signedId;
        element.multipleFiles = node.multipleFiles.map(
          (f) => new File([], f.filename)
        );
        element.multipleFileUrls = node.multipleFiles.map((f) => f.url);
        element.multipleFileSignedIds = node.multipleFiles.map(
          (f) => f.signedId
        );
        currentSection.addElement(element);
      });
    });
  }

  addSection(): Section {
    const section = new Section(this, this.sections.length + 1);
    this.sections.push(section);
    return section;
  }

  mergeUpSection(section: Section): void {
    const targetSection = this.sections[this.sections.indexOf(section) - 1];
    section.elements.forEach((element) => targetSection.addElement(element));
    this.removeSection(section);
  }

  removeSection(section: Section): void {
    const index = this.sections.indexOf(section);
    this.sections.splice(index, 1);

    // Update section number of the next elements from the removed element.
    for (let i = index; i < this.sections.length; i++) {
      this.sections[i].section = i + 1;
    }
  }

  toCreateFormInput(): CreateFormInput {
    let sequence = 0;
    const elementsAttributes: FormElementAttributes[] = [];

    this.sections.forEach((section) => {
      elementsAttributes.push({ sequence, ...section.toAttributes() });
      sequence++;
      section.elements.forEach((element) => {
        elementsAttributes.push({ sequence, ...element.toAttributes() });
        sequence++;
      });
    });

    return {
      title: this.title,
      elementsAttributes,
    };
  }

  toUpdateFormInput(): UpdateFormInput {
    return { ...this.toCreateFormInput(), id: this.id };
  }
}

const formQuery = graphql(`
  query Form($id: ID!) {
    form(id: $id) {
      id
      title
      elements {
        nodes {
          id
          kind
          arguments
          required
          singleFile {
            id
            filename
            signedId
            url
          }
          multipleFiles {
            id
            filename
            signedId
            url
          }
        }
      }
    }
  }
`);
